/**
 * Weather on the staff map (docs/Weather.md, docs/Rendering.md § Weather):
 *  - `SkyCells` interpolates the view's weather cells between ticks and packs the ones over the
 *    screen into uniforms;
 *  - `SkyField` renders them once per frame into a quarter-resolution screen texture (cloud
 *    deck, rain, snow — the engine's precipitation field, clipped to the neatline), which the
 *    light layer (cloud shadows, storm darkening, lightning) and the sky layer sample: the
 *    full-resolution passes stay O(1) per pixel however many systems are on screen;
 *  - `SkyLayer`: a screen-space quad between the lit map and the live layer — animated rain
 *    streaks slanted by the wind, drifting snowflakes in snow, heavier and longer in storms;
 *    density follows the same precipitation field the rules use. Still (no animation) under
 *    reduced motion;
 *  - `IceLayer`: frozen river stretches drawn as pale ice with hairline cracks;
 *  - `groundBits`: the condition bits the overlay shader paints (wet ground, mud and puddles,
 *    snow, frost, morning haze), plus ice on lakes that lie in frozen country.
 * The ground itself (mud sheen, snow, haze) lives in the overlay shader (layers/overlay.ts).
 */
import {
  cellIntensity,
  type GameMap,
  HEX_W,
  type HexGrid,
  hexTemperature,
  isWater,
  SNOW_TEMP,
  Terrain,
  W_FROZEN,
  type WeatherCell,
  type WeatherView,
} from '@friction/engine';
import { type Geometry, Mesh, type Renderer, RenderTexture, type Shader } from 'pixi.js';
import { chaikin } from '../geom/paths';
import { destroyMesh, FRAG_HEADER, makeShader } from '../gfx/gl';
import { LineBatch } from '../gfx/lines';
import { GLSL_COMMON } from '../shaders/common';
import { WORLD_QUAD_VERT } from '../shaders/terrain';
import type { Rect } from '../sheet';
import { quadGeometry, setQuad } from './terrain';

/** Cells the shaders take at most (the ones over the screen first). */
export const MAX_SKY_CELLS = 12;

/** GLSL: the engine's precipitation field (core/weather.ts `cellPrecipAt`) over packed cells. */
export const GLSL_SKY = /* glsl */ `
uniform vec4 uCellA[${MAX_SKY_CELLS}];
uniform vec4 uCellB[${MAX_SKY_CELLS}];
uniform float uCells;
uniform vec4 uNeat;
// Weather belongs to the map, not the table: 1 inside the neatline, fading out over ~10 units.
float onMap(vec2 w) {
  vec2 lo = smoothstep(uNeat.xy - 10.0, uNeat.xy + 4.0, w);
  vec2 hi = 1.0 - smoothstep(uNeat.zw - 4.0, uNeat.zw + 10.0, w);
  return lo.x * lo.y * hi.x * hi.y;
}
// Squared elliptical distance of world point w from cell i (1 = the rim of its rain).
float cellQ(int i, vec2 w) {
  vec4 a = uCellA[i];
  vec4 b = uCellB[i];
  vec2 d = w - a.xy;
  float u = dot(d, a.zw) / b.x;
  float v = (d.y * a.z - d.x * a.w) / b.y;
  return u * u + v * v;
}
`;

/** Interpolated, culled and packed weather cells for one frame. */
export class SkyCells {
  /** (x, y, ux, uy) in world units per cell. */
  readonly a = new Float32Array(MAX_SKY_CELLS * 4);
  /** (major, minor, intensity, snow 0/1) per cell. */
  readonly b = new Float32Array(MAX_SKY_CELLS * 4);
  count = 0;
  /** Strongest storm intensity over the screen (lightning, thunder). */
  storm = 0;
  private readonly prev = new Map<number, WeatherCell>();
  private lastPrev: readonly WeatherCell[] | null = null;

  update(
    w: WeatherView | null,
    prevW: WeatherView | null,
    t: number,
    view: Rect,
    map: GameMap,
    grid: HexGrid,
  ): void {
    this.count = 0;
    this.storm = 0;
    if (!w) return;
    if (prevW?.cells !== this.lastPrev) {
      this.prev.clear();
      for (const c of prevW?.cells ?? []) this.prev.set(c.id, c);
      this.lastPrev = prevW?.cells ?? null;
    }
    const items: {
      x: number;
      y: number;
      ux: number;
      uy: number;
      ma: number;
      mi: number;
      i: number;
      snow: number;
    }[] = [];
    for (const c of w.cells) {
      const p = this.prev.get(c.id);
      const jump = p ? Math.abs(p.x - c.x) + Math.abs(p.y - c.y) > 6 : false;
      const k = p && !jump ? t : 1;
      const x = (p && !jump ? p.x + (c.x - p.x) * k : c.x) * HEX_W;
      const y = (p && !jump ? p.y + (c.y - p.y) * k : c.y) * HEX_W;
      const i0 = p ? cellIntensity(p) : 0;
      const intensity = i0 + (cellIntensity(c) - i0) * t;
      if (intensity < 0.02) continue;
      const r = Math.max(c.major, c.minor) * HEX_W * 1.5;
      if (x + r < view.x0 || x - r > view.x1 || y + r < view.y0 || y - r > view.y1) continue;
      // Snow or rain: the air at the ground under the cell's centre.
      const h = grid.fromWorld(x, y);
      const elev = h >= 0 ? map.elevation[h] : 64;
      const snow = hexTemperature(w.temperature, elev) <= SNOW_TEMP ? 1 : 0;
      items.push({ x, y, ux: c.ux, uy: c.uy, ma: c.major * HEX_W, mi: c.minor * HEX_W, i: intensity, snow });
    }
    items.sort((p, q) => q.i - p.i);
    const n = Math.min(MAX_SKY_CELLS, items.length);
    for (let j = 0; j < n; j++) {
      const c = items[j];
      this.a.set([c.x, c.y, c.ux, c.uy], j * 4);
      this.b.set([c.ma, c.mi, c.i, c.snow], j * 4);
      if (!c.snow) this.storm = Math.max(this.storm, c.i);
    }
    this.count = n;
  }
}

// ---- the sky field ----------------------------------------------------------------------------

/** Field texels per css px: a quarter-resolution screen texture. */
const FIELD_SCALE = 0.25;

const FIELD_FRAG = /* glsl */ `${FRAG_HEADER}
in vec2 vWorld;
uniform vec2 uFieldSize;
uniform vec2 uScreen;
uniform vec3 uCam;
${GLSL_SKY}
void main() {
  vec2 sp = vWorld / uFieldSize * uScreen;
  vec2 w = uCam.xy + (sp - 0.5 * uScreen) / uCam.z;
  float edge = onMap(w);
  float cloud = 0.0;
  float rain = 0.0;
  float snow = 0.0;
  for (int i = 0; i < ${MAX_SKY_CELLS}; i++) {
    if (float(i) >= uCells) break;
    float q = cellQ(i, w);
    // The cloud deck reaches well beyond the rain.
    float qc = q * 0.45;
    if (qc >= 1.0) continue;
    float k = uCellB[i].z;
    cloud = max(cloud, (1.0 - qc) * (1.0 - qc) * min(1.0, 0.35 + 1.3 * k));
    if (q >= 1.0) continue;
    float v = k * (1.0 - q) * (1.0 - q);
    if (uCellB[i].w > 0.5) snow = max(snow, v);
    else rain = max(rain, v);
  }
  finalColor = vec4(cloud * edge, rain * edge, snow * edge, 1.0);
}
`;

/** Clouds (r), rain (g) and snow (b) over the screen at quarter resolution. */
export class SkyField {
  readonly texture: RenderTexture;
  private readonly mesh: Mesh<Geometry, Shader>;
  private readonly shader: Shader;
  private w = 1;
  private h = 1;

  constructor() {
    this.texture = RenderTexture.create({ width: 1, height: 1, resolution: 1, scaleMode: 'linear' });
    this.shader = makeShader('friction-sky-field', WORLD_QUAD_VERT, FIELD_FRAG, {
      fieldUniforms: {
        uFieldSize: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
        uScreen: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
        uCam: { value: new Float32Array([0, 0, 1]), type: 'vec3<f32>' },
        uCellA: { value: new Float32Array(MAX_SKY_CELLS * 4), type: 'vec4<f32>', size: MAX_SKY_CELLS },
        uCellB: { value: new Float32Array(MAX_SKY_CELLS * 4), type: 'vec4<f32>', size: MAX_SKY_CELLS },
        uCells: { value: 0, type: 'f32' },
        uNeat: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
      },
    });
    this.mesh = new Mesh({ geometry: quadGeometry({ x0: 0, y0: 0, x1: 1, y1: 1 }), shader: this.shader });
    this.mesh.blendMode = 'none';
  }

  resize(w: number, h: number): void {
    if (w === this.w && h === this.h) return;
    this.w = w;
    this.h = h;
    const fw = Math.max(1, Math.ceil(w * FIELD_SCALE));
    const fh = Math.max(1, Math.ceil(h * FIELD_SCALE));
    this.texture.resize(fw, fh, 1);
    setQuad(this.mesh.geometry, { x0: 0, y0: 0, x1: fw, y1: fh });
    const u = this.shader.resources.fieldUniforms.uniforms;
    (u.uFieldSize as Float32Array).set([fw, fh]);
    (u.uScreen as Float32Array).set([w, h]);
  }

  setNeat(r: Rect): void {
    (this.shader.resources.fieldUniforms.uniforms.uNeat as Float32Array).set([r.x0, r.y0, r.x1, r.y1]);
  }

  /** Render this frame's field (skipped when no cell is over the screen). */
  render(renderer: Renderer, cells: SkyCells, cam: { x: number; y: number; zoom: number }): void {
    if (!cells.count) return;
    const u = this.shader.resources.fieldUniforms.uniforms;
    (u.uCam as Float32Array).set([cam.x, cam.y, cam.zoom]);
    (u.uCellA as Float32Array).set(cells.a);
    (u.uCellB as Float32Array).set(cells.b);
    u.uCells = cells.count;
    renderer.render({ container: this.mesh, target: this.texture, clear: true });
  }

  destroy(): void {
    destroyMesh(this.mesh);
    this.texture.destroy(true);
  }
}

// ---- rain & snow ------------------------------------------------------------------------------

const SKY_FRAG = /* glsl */ `${FRAG_HEADER}
in vec2 vWorld;
uniform vec2 uScreen;
uniform float uTime;
uniform vec2 uFall;
uniform float uNight;
uniform float uFade;
uniform sampler2D uSky;
${GLSL_COMMON}

/** Rain streaks in screen px: \`density\` of the lattice cells hold a streak. */
float streaks(vec2 sp, float spacing, float len, float speed, float density) {
  vec2 r = vec2(dot(sp, vec2(uFall.y, -uFall.x)), dot(sp, uFall) - uTime * speed);
  vec2 cell = vec2(spacing, len * 2.4);
  vec2 id = floor(r / cell);
  vec3 h = vec3(hash22(id), hash12(id + 7.0));
  if (h.z > density) return 0.0;
  vec2 f = r / cell - id;
  float dx = abs(f.x - (0.15 + 0.7 * h.x)) * cell.x;
  float along = (f.y - 0.5 * h.y) * cell.y;
  return clamp(1.0 - dx / 0.8, 0.0, 1.0) * clamp(along * 0.25, 0.0, 1.0) * clamp((len - along) / (len * 0.45), 0.0, 1.0);
}

/** Drifting snowflakes: soft dots that sway as they fall. */
float flakes(vec2 sp, float spacing, float speed, float density, float seed) {
  vec2 r = vec2(sp.x + sin(uTime * 0.9 + sp.y * 0.013 + seed) * 7.0, sp.y - uTime * speed);
  vec2 id = floor(r / spacing);
  vec2 f = r / spacing - id;
  if (hash12(id + seed) > density) return 0.0;
  vec2 c = vec2(0.2, 0.2) + 0.6 * hash22(id + seed + 5.0);
  float d = length((f - c) * spacing);
  float rad = 1.1 + 0.9 * hash12(id + seed + 9.0);
  return 1.0 - smoothstep(rad - 0.7, rad + 0.7, d);
}

void main() {
  vec2 sp = vWorld;
  vec4 field = texture(uSky, sp / uScreen);
  float p = max(field.g, field.b);
  if (p < 0.06) discard;
  float snow = field.b > field.g ? 1.0 : 0.0;
  float dens = smoothstep(0.06, 0.85, p);
  float heavy = smoothstep(0.55, 0.9, p);
  vec4 outc;
  if (snow > 0.5) {
    float f = flakes(sp, 23.0, 38.0 + 60.0 * heavy, 0.25 + 0.6 * dens, 3.0)
            + 0.6 * flakes(sp + 11.0, 15.0, 26.0 + 40.0 * heavy, 0.2 + 0.5 * dens, 7.0);
    float a = clamp(f, 0.0, 1.0) * mix(0.72, 0.5, uNight) * uFade;
    outc = vec4(vec3(0.98, 0.99, 1.0) * a, a);
  } else {
    float s = streaks(sp, 8.0, 15.0 + 12.0 * heavy, 520.0 + 280.0 * heavy, 0.2 + 0.6 * dens);
    float a = clamp(s, 0.0, 1.0) * mix(0.42, 0.22, uNight) * (0.7 + 0.5 * heavy) * uFade;
    vec3 c = mix(vec3(0.2, 0.25, 0.32), vec3(0.72, 0.8, 0.92), uNight);
    outc = vec4(c * a, a);
  }
  finalColor = outc;
}
`;

export class SkyLayer {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly shader: Shader;
  private w = 1;
  private h = 1;
  private time = 0;

  constructor(field: SkyField) {
    this.shader = makeShader('friction-sky', WORLD_QUAD_VERT, SKY_FRAG, {
      uSky: field.texture.source,
      skyUniforms: {
        uScreen: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
        uTime: { value: 0, type: 'f32' },
        uFall: { value: new Float32Array([0, 1]), type: 'vec2<f32>' },
        uNight: { value: 0, type: 'f32' },
        uFade: { value: 1, type: 'f32' },
      },
    });
    this.mesh = new Mesh({ geometry: quadGeometry({ x0: 0, y0: 0, x1: 1, y1: 1 }), shader: this.shader });
    this.mesh.visible = false;
  }

  resize(w: number, h: number): void {
    if (w === this.w && h === this.h) return;
    this.w = w;
    this.h = h;
    setQuad(this.mesh.geometry, { x0: 0, y0: 0, x1: w, y1: h });
    const s = this.shader.resources.skyUniforms.uniforms.uScreen as Float32Array;
    s[0] = w;
    s[1] = h;
  }

  /**
   * Per frame. `wind` (hex units / h) slants the streaks; `still` (reduced motion) freezes them.
   */
  update(
    cells: SkyCells,
    cam: { x: number; y: number; zoom: number },
    wind: { x: number; y: number },
    night: number,
    dt: number,
    still: boolean,
  ): void {
    this.mesh.visible = cells.count > 0;
    if (!this.mesh.visible) return;
    if (!still) this.time += dt;
    const u = this.shader.resources.skyUniforms.uniforms;
    u.uTime = this.time;
    u.uNight = night;
    // Rain falls a little downwind; streaks fade to a veil when the whole theatre is in view.
    const sx = Math.max(-0.45, Math.min(0.45, wind.x * 0.5));
    const len = Math.sqrt(1 + sx * sx);
    (u.uFall as Float32Array).set([sx / len, 1 / len]);
    u.uFade = (0.4 + 0.6 * Math.min(1, Math.max(0, (cam.zoom - 0.3) / 0.6))) * (still ? 0.7 : 1);
  }

  destroy(): void {
    destroyMesh(this.mesh);
  }
}

// ---- ice ------------------------------------------------------------------------------------

/** Frozen river stretches: pale ice inside the river line, with hairline cracks. */
export class IceLayer {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly batch = new LineBatch('river-ice', 8192);
  private signature = '';

  constructor() {
    this.mesh = this.batch.mesh;
  }

  /** Rebuild when the frozen state of any river edge changed. */
  update(map: GameMap, grid: HexGrid, conditions: Uint8Array | null): void {
    /** 0 = open water or unfrozen land, 1 = frozen land, 2 = water / off the map (no bank). */
    const bank = (x: number, y: number): number => {
      const h = grid.fromWorld(x, y);
      if (h < 0 || isWater(map.terrain[h])) return 2;
      return conditions && conditions[h] & W_FROZEN ? 1 : 0;
    };
    // Per river, per segment (a hex edge): frozen when no bank is thawed and one is frozen —
    // the engine's rule (both banks frozen) where the river runs between land hexes.
    const flags: number[][] = [];
    let sig = '';
    for (const river of map.rivers) {
      const pts = river.points;
      const segs: number[] = [];
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const mx = (pts[i] + pts[i + 2]) / 2;
        const my = (pts[i + 1] + pts[i + 3]) / 2;
        const l = Math.hypot(pts[i + 3] - pts[i + 1], pts[i + 2] - pts[i]) || 1;
        const nx = (-(pts[i + 3] - pts[i + 1]) / l) * 12;
        const ny = ((pts[i + 2] - pts[i]) / l) * 12;
        const a = bank(mx + nx, my + ny);
        const b = bank(mx - nx, my - ny);
        const f = a !== 0 && b !== 0 && (a === 1 || b === 1) ? 1 : 0;
        segs.push(f);
        sig += f;
      }
      flags.push(segs);
    }
    if (sig === this.signature) return;
    this.signature = sig;
    const b = this.batch;
    b.clear();
    map.rivers.forEach((river, r) => {
      const segs = flags[r];
      if (!segs.some(Boolean) || river.points.length < 4) return;
      // Vertex value: frozen if a segment on either side is.
      const n = river.points.length >> 1;
      const v = Array.from({ length: n }, (_, i) => Math.max(segs[i - 1] ?? 0, segs[i] ?? 0));
      const { pts, values: widthV } = chaikin(river.points, 3, false, river.widths);
      const { values: frozenV } = chaikin(river.points, 3, false, v);
      let run: number[] = [];
      let widths: number[] = [];
      const flush = () => {
        if (run.length >= 4) {
          b.add(run, { width: 1.5, color: 0xdbe6ea, alpha: 0.95, minPx: 0.7, maxPx: 6 }, { widths });
          b.add(run, {
            width: 0.35,
            color: 0x5d7684,
            alpha: 0.75,
            minPx: 0.4,
            maxPx: 1,
            dash: 5,
            gap: 7,
          });
        }
        run = [];
        widths = [];
      };
      for (let i = 0; i < pts.length >> 1; i++) {
        if ((frozenV?.[i] ?? 0) >= 0.5) {
          run.push(pts[i * 2], pts[i * 2 + 1]);
          widths.push(0.7 + (widthV?.[i] ?? 1) * 0.9);
        } else flush();
      }
      flush();
    });
    b.commit();
  }

  setZoom(zoomCss: number, resolution: number): void {
    this.batch.setZoom(zoomCss, resolution);
  }

  destroy(): void {
    this.batch.destroy();
  }
}

// ---- ground bits for the overlay ------------------------------------------------------------

let groundCache: { src: Uint8Array; out: Uint8Array } | null = null;

/**
 * The view's condition bits for the overlay, plus W_FROZEN on lake hexes whose shores are
 * mostly frozen (the engine keeps water hexes at 0: nobody walks on lakes).
 */
export function groundBits(map: GameMap, grid: HexGrid, conditions: Uint8Array): Uint8Array {
  if (groundCache?.src === conditions) return groundCache.out;
  const out = conditions.slice();
  for (let h = 0; h < grid.count; h++) {
    if (map.terrain[h] !== Terrain.Lake) continue;
    let frozen = 0;
    let shore = 0;
    for (const n of grid.neighbourList(h)) {
      if (isWater(map.terrain[n])) continue;
      shore++;
      if (conditions[n] & W_FROZEN) frozen++;
    }
    if (shore > 0 ? frozen * 2 >= shore : false) out[h] |= W_FROZEN;
  }
  // Inner lake hexes (no shore) take the ice of their frozen neighbours.
  for (let h = 0; h < grid.count; h++) {
    if (map.terrain[h] !== Terrain.Lake || out[h] & W_FROZEN) continue;
    const ns = grid.neighbourList(h);
    if (ns.length && ns.every((n) => map.terrain[n] !== Terrain.Lake || out[n] & W_FROZEN)) {
      if (ns.some((n) => out[n] & W_FROZEN)) out[h] |= W_FROZEN;
    }
  }
  groundCache = { src: conditions, out };
  return out;
}
